import React from 'react';
import styled from 'styled-components';
import { ModalType, VenueType } from '../../../shared/enum/EnumRepository';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useAdminVenueStore } from '../../share/store/adminVenueStore';
import { useAdminVenueTag } from '../../share/hook/useAdminVenueTag';
import { getDefaultVenueFilterData } from './buildVenueListParams';
import filterIcon from '../../../asset/icon/filter.png';
import { runAfterVenueListContextChange } from './runAfterUnsavedVenueCheck';

const FilterStationVenue: React.FC = () => {
  const { setStandardModal } = useModalStore();
  const { tags } = useAdminVenueTag();
  const filterData = useAdminVenueStore((state) => state.filterData);
  const setFilterData = useAdminVenueStore((state) => state.setFilterData);

  const getTypeDisplayText = () => {
    const selected = Object.entries(filterData.venueType)
      .filter(([, isSelected]) => isSelected)
      .map(([type]) => type);
    if (selected.length === Object.values(VenueType).length) return 'ALL';
    if (selected.length <= 2) return selected.join(', ');
    return `${selected.slice(0, 2).join(', ')} +${selected.length - 2}`;
  };

  const getTagDisplayText = () => {
    const names = filterData.tagIds.map((tagId) =>
      tags.find((tag) => tag.id === tagId)?.name ?? `#${tagId}`
    );
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  };

  const handleFilter = () => {
    setStandardModal({
      modalType: ModalType.FILTER_STATION_VENUE,
      title: 'Filter',
      value: '',
      handleChange: () => { },
    });
  };

  const removeFilterFactor = (
    factor: 'venueType' | 'admissionFree' | 'openToday' | 'tagIds' | 'districts',
  ) => {
    const defaults = getDefaultVenueFilterData();
    let next = filterData;
    switch (factor) {
      case 'venueType':
        next = { ...filterData, venueType: defaults.venueType };
        break;
      case 'admissionFree':
        next = { ...filterData, admissionFree: false };
        break;
      case 'openToday':
        next = { ...filterData, openToday: false };
        break;
      case 'tagIds':
        next = { ...filterData, tagIds: [] };
        break;
      case 'districts':
        next = { ...filterData, districts: [] };
        break;
    }
    runAfterVenueListContextChange(() => {
      setFilterData(next);
    });
  };

  const hasTypeFilter = Object.values(filterData.venueType).some((isSelected) => !isSelected);
  const hasAdmissionFilter = filterData.admissionFree;
  const hasOpenTodayFilter = filterData.openToday;
  const hasTagFilter = filterData.tagIds.length > 0;
  const hasDistrictFilter = (filterData.districts ?? []).length > 0;

  const getDistrictDisplayText = () => {
    const names = filterData.districts ?? [];
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  };

  return (
    <FilterStationComp>
      {(hasTypeFilter || hasAdmissionFilter || hasOpenTodayFilter || hasTagFilter || hasDistrictFilter) && (
        <FilterFactorContainer>
          {hasTagFilter && (
            <FilterFactor onClick={() => removeFilterFactor('tagIds')}>
              Tag: {getTagDisplayText()}
            </FilterFactor>
          )}
          {hasDistrictFilter && (
            <FilterFactor onClick={() => removeFilterFactor('districts')}>
              District: {getDistrictDisplayText()}
            </FilterFactor>
          )}
          {hasTypeFilter && (
            <FilterFactor onClick={() => removeFilterFactor('venueType')}>
              Type: {getTypeDisplayText()}
            </FilterFactor>
          )}
          {hasAdmissionFilter && (
            <FilterFactor onClick={() => removeFilterFactor('admissionFree')}>
              Admission: Free
            </FilterFactor>
          )}
          {hasOpenTodayFilter && (
            <FilterFactor onClick={() => removeFilterFactor('openToday')}>
              Today open
            </FilterFactor>
          )}
        </FilterFactorContainer>
      )}
      <FilterCaption onClick={handleFilter}>
        <IconBtn $backgroundImg={filterIcon} />
        Filter
      </FilterCaption>
    </FilterStationComp>
  );
};

export default FilterStationVenue;

const FilterStationComp = styled.section`
  width: fit-content;
  height: calc(2vh);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterCaption = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
  gap: 4px;
`;

const FilterFactorContainer = styled.div`
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-right: 8px;
  border-right: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const FilterFactor = styled.div`
  width: fit-content;
  border-bottom: 1px dotted ${({ theme }) => theme.colors.color_Gray_05};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
`;

const IconBtn = styled.div<{ $backgroundImg: string }>`
  width: clamp(10px, 2vw, 12px);
  height: clamp(10px, 2vw, 12px);
  background-image: url(${({ $backgroundImg }) => $backgroundImg});
  background-size: cover;
  background-position: center;
`;
